import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import PageHeader from '../components/headers/Header';
import { Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import {
  captureClientError,
  getCapturedClientError,
  reportClientErrorToTeams,
  SUPPORT_RECIPIENTS
} from '../utils/errorReporting';

const ITSupportPage = () => {
  const navigate = useNavigate();
  const [isReporting, setIsReporting] = useState(true);
  const [reportFailed, setReportFailed] = useState(false);

  const errorDetails = useMemo(() => {
    const capturedError = getCapturedClientError();

    return capturedError || captureClientError('The user was redirected to the IT error page.', {
      component: 'ITSupportPage',
      page: window.location.pathname
    });
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const sendReport = async () => {
      setIsReporting(true);
      setReportFailed(false);

      try {
        await reportClientErrorToTeams(errorDetails);
      } catch (error) {
        console.error('Failed to report client error to Teams:', error);
        if (!isCancelled) setReportFailed(true);
      } finally {
        if (!isCancelled) setIsReporting(false);
      }
    };

    sendReport();

    return () => {
      isCancelled = true;
    };
  }, [errorDetails]);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        pt: 4
      }}
    >
      <Container maxWidth="md">
        <PageHeader
          title="Page Issue"
          subtitle="Something went wrong while opening this page."
        />

        <Paper
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: '24px',
            textAlign: 'center',
            boxShadow: '0 10px 40px rgba(0,0,0,0.04)',
            border: '1px solid #e2e8f0',
            mt: 4
          }}
        >
          <Box
            sx={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              backgroundColor: '#fee2e2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 32px'
            }}
          >
            <AlertCircle size={50} color="#dc2626" />
          </Box>

          <Typography variant="h3" sx={{ fontWeight: 800, color: '#1e293b', mb: 2 }}>
            This Page Hit a Problem
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: '#64748b',
              mb: 5,
              maxWidth: '560px',
              mx: 'auto',
              lineHeight: 1.7,
              fontSize: '1.1rem'
            }}
          >
            We could not finish loading the page you were trying to visit. The problem has
            been reported to IT with the error message, page URL, and component details when
            available.
          </Typography>

          <Box
            sx={{
              backgroundColor: reportFailed ? '#fff7ed' : '#f1f5f9',
              border: `1px solid ${reportFailed ? '#fed7aa' : '#e2e8f0'}`,
              borderRadius: '16px',
              p: 2.5,
              mb: 5,
              maxWidth: '560px',
              mx: 'auto'
            }}
          >
            <Typography sx={{ color: reportFailed ? '#9a3412' : '#475569', fontWeight: 600 }}>
              {isReporting && 'Reporting the issue to IT...'}
              {!isReporting && !reportFailed && `Reported to ${SUPPORT_RECIPIENTS.join(' and ')}.`}
              {!isReporting && reportFailed && 'The page captured the error, but the report could not be sent automatically.'}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}
          >
            <Button
              variant="outlined"
              onClick={handleGoBack}
              startIcon={<ArrowLeft size={20} />}
              sx={{
                borderColor: '#e2e8f0',
                color: '#475569',
                '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f1f5f9' },
                borderRadius: '12px',
                padding: '12px 24px',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: 150
              }}
            >
              Go Back
            </Button>

            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              startIcon={isReporting ? <CircularProgress size={20} color="inherit" /> : <RefreshCw size={20} />}
              sx={{
                backgroundColor: '#ef4444',
                '&:hover': { backgroundColor: '#dc2626' },
                borderRadius: '12px',
                padding: '12px 24px',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: 150
              }}
            >
              Refresh
            </Button>
          </Box>

          <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid #f1f5f9' }}>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              Error reference: {errorDetails.reportedAt}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ITSupportPage;
